export type MissiveOpenRequest = {
  token: string;
  phone?: string;
  email?: string;
  name?: string;
  leadId?: string;
  whatsapp?: import('lib/missive/whatsapp-compose').WhatsAppComposePayload;
  emailCompose?: import('lib/missive/email-compose').EmailComposePayload;
  createdAt: number;
};

const REQUEST_TTL_MS = 2 * 60 * 1000;

function storageKey(requestId: string) {
  return `cc-missive-open:${requestId}`;
}

function createRequestId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type MissiveOpenQuery = {
  phone?: string;
  email?: string;
  name?: string;
  leadId?: string;
  whatsapp?: import('lib/missive/whatsapp-compose').WhatsAppComposePayload;
  emailCompose?: import('lib/missive/email-compose').EmailComposePayload;
};

export function stageMissiveOpenRequest(
  requestId: string,
  query: MissiveOpenQuery,
  token: string,
) {
  const request: MissiveOpenRequest = {
    token,
    phone: query.phone?.trim() || undefined,
    email: query.email?.trim() || undefined,
    name: query.name?.trim() || undefined,
    leadId: query.leadId?.trim() || undefined,
    whatsapp: query.whatsapp,
    emailCompose: query.emailCompose,
    createdAt: Date.now(),
  };

  localStorage.setItem(storageKey(requestId), JSON.stringify(request));
}

export function readMissiveOpenRequest(
  requestId: string,
): MissiveOpenRequest | null {
  const raw = localStorage.getItem(storageKey(requestId));

  if (!raw) {
    return null;
  }

  try {
    const request = JSON.parse(raw) as MissiveOpenRequest;

    if (!request.token || Date.now() - request.createdAt > REQUEST_TTL_MS) {
      localStorage.removeItem(storageKey(requestId));
      return null;
    }

    return request;
  } catch {
    localStorage.removeItem(storageKey(requestId));
    return null;
  }
}

export function clearMissiveOpenRequest(requestId: string) {
  localStorage.removeItem(storageKey(requestId));
}

export function buildMissiveOpenPath(
  requestId: string,
  query: MissiveOpenQuery,
) {
  const params = new URLSearchParams({ request: requestId });

  if (query.phone?.trim()) {
    params.set('phone', query.phone.trim());
  }

  if (query.email?.trim()) {
    params.set('email', query.email.trim());
  }

  if (query.name?.trim()) {
    params.set('name', query.name.trim());
  }

  if (query.leadId?.trim()) {
    params.set('leadId', query.leadId.trim());
  }

  return `/missive/open?${params.toString()}`;
}

export function openMissiveInNewTab(query: MissiveOpenQuery, token: string) {
  const requestId = createRequestId();
  stageMissiveOpenRequest(requestId, query, token);
  const path = buildMissiveOpenPath(requestId, query);
  const tab = window.open(path, '_blank');

  if (tab) {
    tab.focus();
    return true;
  }

  window.location.assign(path);
  return true;
}
